import { useState } from "react";

import { toBrowserLocalDay, toISODateTime } from "common/utils/dates";
import { SubscriptionFrequency, UserSubscriptionAction } from "common/types";

import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import Calendar from "client/common/components/calendars/Calendar";
import { useCurrentDrawer } from "client/common/hooks/stackables";
import { useFeatures } from "client/clinics/hooks";

import { useSubscriptionQuery, useUpdateSubscriptionMutation } from "../../hooks";
import { DetailSubscription } from "../../types";
import {
  doesSubscriptionRequireAction,
  minSelectableDeliveryDate,
  minSelectableNextOrderDate,
} from "../../utils";
import SubscriptionFrequencySelect from "../SubscriptionFrequencySelect";
import SubscriptionAlert from "../SubscriptionAlert";

export type SubscriptionResumeDrawerProps = {
  id: number;
};

type SubscriptionResumeFormProps = {
  subscription: DetailSubscription;
};

const SubscriptionResumeForm = ({ subscription }: SubscriptionResumeFormProps) => {
  const { close } = useCurrentDrawer();
  const isEdbEnabled = useFeatures().AUTOSHIP_EDB;
  const isGranularControlsEnabled = useFeatures().CONTROLLED_SUBSTANCE_GRANULAR_CONTROLS;
  const {
    mutate: updateSubscription,
    isPending: isLoading,
    error,
  } = useUpdateSubscriptionMutation(subscription.id, {
    onSuccess: close,
  });

  // v2 delivery dates cannot fall inside the min lead-time window; v1 resume is a
  // new date pick, so the earliest selectable next order date is tomorrow.
  const [scheduleDate, setScheduleDate] = useState(
    isEdbEnabled ? minSelectableDeliveryDate() : minSelectableNextOrderDate(),
  );
  const [frequencyValues, setFrequencyValues] = useState<SubscriptionFrequency>(
    subscription.is_active_frequency_valid
      ? subscription.active_frequency
      : subscription.recommended_frequency,
  );

  return (
    <DrawerLayout>
      <DrawerLayout.Header
        title="Resume autoship"
        description={
          isEdbEnabled
            ? "Enable your Autoship again by selecting your next estimated delivery date and frequency"
            : "Enable your Autoship again by selecting your next order date and frequency"
        }
        onClose={close}
      />
      <DrawerLayout.Form
        size="l"
        canSubmit={!doesSubscriptionRequireAction(subscription, isGranularControlsEnabled)}
        onSubmit={() =>
          updateSubscription({
            action: UserSubscriptionAction.Resume,
            ...(isEdbEnabled
              ? { expected_delivery_by: toISODateTime(scheduleDate) }
              : { next_order_date: toISODateTime(scheduleDate) }),
            ...frequencyValues,
          })
        }
        submitText="Resume autoship"
        isLoading={isLoading}
        error={error}
        fieldErrors={[
          error?.errors.action,
          isEdbEnabled ? error?.errors.expected_delivery_by : error?.errors.next_order_date,
          error?.errors.frequency,
          error?.errors.interval_unit,
        ]}
      >
        {doesSubscriptionRequireAction(subscription, isGranularControlsEnabled) && (
          <DrawerLayout.Alerts>
            <SubscriptionAlert subscription={subscription} />
          </DrawerLayout.Alerts>
        )}
        <DrawerLayout.Form.Group>
          <DrawerLayout.Form.Label>
            {isEdbEnabled
              ? "Select your next estimated delivery date"
              : "Select your next order date"}
          </DrawerLayout.Form.Label>
          <Calendar
            disabledDays={{
              before: toBrowserLocalDay(
                isEdbEnabled ? minSelectableDeliveryDate() : minSelectableNextOrderDate(),
              ),
            }}
            selectedDays={toBrowserLocalDay(scheduleDate)}
            onSelect={setScheduleDate}
          />
        </DrawerLayout.Form.Group>
        <DrawerLayout.Form.Group>
          <DrawerLayout.Form.Label>Select your order frequency</DrawerLayout.Form.Label>
          <SubscriptionFrequencySelect
            subscription={subscription}
            value={frequencyValues}
            onSelect={setFrequencyValues}
          />
        </DrawerLayout.Form.Group>
      </DrawerLayout.Form>
    </DrawerLayout>
  );
};

const SubscriptionResumeDrawer = ({ id }: SubscriptionResumeDrawerProps) => {
  const { data: subscription, isLoading, error } = useSubscriptionQuery(id);

  if (isLoading) {
    return <DrawerLayout.Loading />;
  }

  if (!subscription || error) {
    return <DrawerLayout.FetchError entityName="Autoship" error={error} />;
  }

  return <SubscriptionResumeForm subscription={subscription} />;
};

export default SubscriptionResumeDrawer;
